import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from './useTenants';
import { classifyEditTenantNameError, useEditTenantName } from './useEditTenantName';

/**
 * SLICE 55 — change a tenant's customer-facing ACCOUNT NAME. Super-admin only,
 * opened solely from the super-admin action in `Tenants`.
 *
 * Mirrors `EditLoginDialog` (slice 52): a modal over the list, the field PREFILLED
 * with the stored value verbatim, and a submit that is disabled until there is a
 * real, DIFFERENT value. There is no picker — the tenant is the subject — so it is
 * the simpler sibling: type a new name, review, confirm.
 *
 * The client does a low floor only (non-empty, trimmed, actually changed); the
 * SERVER is the real validator (it trims, bounds to 120 chars, and 404s an unknown
 * tenant), and the error copy comes from `classifyEditTenantNameError`.
 */

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

export function EditAccountNameDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(tenant.name);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ name: string } | null>(null);
  const edit = useEditTenantName();

  const trimmed = name.trim();
  // A real, different name. An unchanged value or an empty one is a no-op the
  // operator did not mean to submit.
  const ready = trimmed.length > 0 && trimmed !== tenant.name.trim();

  async function submit() {
    if (!ready) return;
    setError(null);
    try {
      const result = await edit.mutateAsync({ tenantId: tenant.id, name: trimmed });
      setDone({ name: result.name });
    } catch (e) {
      setError(t(`editAccountName.error.${classifyEditTenantNameError(e)}`));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('editAccountName.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('editAccountName.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('editAccountName.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12.5px]">
              {t('editAccountName.done', { name: done.name })}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white"
              >
                {t('editAccountName.doneButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('editAccountName.intro')}
            </p>

            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('editAccountName.name')}
              </span>
              <input
                className={INPUT}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                autoFocus
                autoComplete="off"
              />
            </label>

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={edit.isPending}
                className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
              >
                {t('editAccountName.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={!ready || edit.isPending}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {edit.isPending ? t('editAccountName.saving') : t('editAccountName.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
